import { useNavigate } from 'react-router-dom'
import { Layout } from '@/shell/Layout'
import type { StateWorkspaceSummary } from '@/lib/apiTypes'
import { useOrgs, useStateWorkspaces, useAccessToken } from '@/lib/hooks'
import { EmptyState } from '@/shared/components/EmptyState'
import { EmptyGuide, useGuideNavigate } from '@/features/integrations/components/EmptyGuide'
import { StoringStateGuide } from '@/features/integrations/guides/StoringStateGuide'
import { GUIDED_ONBOARDING_TIERS } from '@/lib/tiers'
import { SkeletonTable } from '@/shared/components/Skeleton'
import { FeedbackMessage } from '@/shared/components/FeedbackMessage'
import { PageIntro } from '@/shared/components/PageIntro'
import { WorkspaceDetailModal } from '@/features/state/WorkspaceDetailModal'
import { WorkspacesTable } from '@/features/state/components/WorkspacesTable'

// ── Org state view ────────────────────────────────────────────────────────────
export function StateOrgView({ orgSlug, workspaceParam, defaultVersionId }: { orgSlug: string; workspaceParam?: string; defaultVersionId?: string }) {
  const orgs = useOrgs()
  const navigate = useNavigate()
  const goToGuide = useGuideNavigate()
  const { token } = useAccessToken()
  const org = orgs.data?.orgs.find((o) => (o.slug ?? o.id) === orgSlug)
  const canAdmin = ['owner', 'admin'].includes(org?.role ?? '')
  const workspaces = useStateWorkspaces(orgSlug)

  // The detail modal is purely URL-driven (deep-linkable), like ProvidersList's artefact modal -
  // no local selection state to desync from the route.
  //
  // `workspaceParam` arrives already decoded: a workspace key may contain slashes
  // (`stacks/data/platform`) and both this route and the management API match a SINGLE segment, so
  // every link writes the key percent-encoded (see openWorkspace below) and react-router turns the
  // `%2F`s back into slashes before handing the param over. Decoding it again here would be a
  // second pass over an already-decoded key.
  const selected = (workspaceParam && workspaces.data)
    ? workspaces.data.workspaces.find((w) => w.name === workspaceParam) ?? null
    : null

  function openWorkspace(ws: StateWorkspaceSummary) {
    navigate(`/org/${orgSlug}/state/${encodeURIComponent(ws.name)}`)
  }

  return (
    <Layout title="State">
      <PageIntro pageKey="state" title="What is this page?">
        <p style={{ margin: '0 0 10px' }}>
          A Terraform HTTP state backend, scoped to this org. Each workspace stores its state here
          with locking, so <code className="mono">terraform</code>/<code className="mono">tofu</code> can
          push, pull and lock state the same way it would against any remote backend - no separate
          storage to provision or secure.
        </p>
        <p style={{ margin: 0 }}>
          Every pushed state version is scanned for secrets and sensitive values, and policy results
          are reported against it. Browse a workspace's version history, inspect resources and
          outputs, or force-unlock a stuck workspace. To see how workspaces, modules and providers
          relate across the org, open the <a href={`/org/${orgSlug}/graph`} style={{ color: 'var(--accent)', textDecoration: 'none' }}>dependency graph</a>.
        </p>
      </PageIntro>

      {workspaces.loading && (
        <SkeletonTable
          headers={['Workspace', 'Latest serial', 'Resources', 'Scan', 'Findings', 'Policy', 'Pushed by', 'Pushed at', 'Actions']}
          firstColTwoLines={false}
        />
      )}
      {workspaces.error && (
        workspaces.error.includes('quota_exceeded') || workspaces.error.includes('stateBackend') ? (
          <EmptyState icon="◈" title="State backend is not available on this workspace." subtitle="Your plan does not include the state backend. See Billing for what is available." />
        ) : (
          <FeedbackMessage ok={false} msg={workspaces.error} />
        )
      )}

      {!workspaces.loading && !workspaces.error && (
        <>
          {(workspaces.data?.workspaces.length ?? 0) === 0 ? (
            org && GUIDED_ONBOARDING_TIERS.has(org.tier) ? (
              <EmptyGuide
                icon="◈"
                title="No state workspaces yet"
                blurb="Point a Terraform backend at this org and a workspace is created on the first apply. The guide below has the backend block and pipeline snippets for this org."
                guideId="storing-state"
              >
                <StoringStateGuide org={org} token={token} onSwitchGuide={goToGuide} />
              </EmptyGuide>
            ) : (
              <EmptyState icon="◈" title="No state workspaces yet." subtitle="Configure a terraform backend to create workspaces automatically on first apply." />
            )
          ) : (
            <WorkspacesTable
              orgId={orgSlug}
              token={token}
              workspaces={workspaces.data!.workspaces}
              onOpen={openWorkspace}
              onRefresh={workspaces.refetch}
            />
          )}
        </>
      )}

      {selected && (
        <WorkspaceDetailModal
          orgId={orgSlug}
          workspace={selected}
          canAdmin={canAdmin}
          defaultVersionId={defaultVersionId}
          onRefresh={workspaces.refetch}
          onClose={() => navigate(`/org/${orgSlug}/state`)}
        />
      )}
    </Layout>
  )
}
